/**
 * Dynamic Form Section Component
 *
 * Renders form sections dynamically based on TabSection configuration.
 * Uses the EditableField pattern for individual field editing.
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Edit3, Save, X } from 'lucide-react';
import { TabSection, FormSectionConfig, FormField, isFormSection } from '../../../types/tabConfiguration';
import { getFieldMeta, prefetchFieldMetadata } from '../../../services/sfFieldMetadataService';
import { shouldUseGobodo } from '../../../utils/gobodoUtils';
import { gobodoUserProvider } from '../../../services/providers/gobodoUserProvider';
import type { User } from '../../../services/queries/userQueries';
import { StandardLookupField, type StandardLookupSelection } from '../../standard';
import {
  formatEpochOrDateAsUsDate,
  formatEpochOrDateAsUsDateTime,
  looksLikeEpochInstant,
} from '../../../utils/epochDateDisplay';

interface DynamicFormSectionProps {
  /** Section configuration from tab configuration */
  section: TabSection;
  /** Optional record ID for data fetching */
  recordId?: string;
  /** Record data - when provided, used instead of mock */
  recordData?: Record<string, unknown>;
  /** Optional custom class name */
  className?: string;
  /**
   * Persist a field edit. On success the form keeps the value the user saved (optimistic).
   * Return value is optional (e.g. updated record for parent cache invalidation); the form does not overwrite the field from the response.
   */
  onFieldSave?: (fieldName: string, value: unknown) => void | Promise<unknown>;
}

/**
 * Mock field values for demonstration
 * In production, this would come from API/React Query
 */
const getMockFieldValue = (fieldName: string, fieldType: string): any => {
  const mockValues: Record<string, any> = {
    // Client Details - Basic Information
    clientName: 'Acme Corporation',
    clientNumber: 'CS-2024-001',
    email: 'contact@acme.com',
    phone: '(555) 123-4567',
    mobile: '(555) 987-6543',
    status: 'active',
    dateJoined: '2024-01-15',
    notes: 'Premium client with high engagement.',

    // Client Details - Reports & Profession
    creditScore: '750',
    lastReportDate: '2024-10-01',
    profession: 'Software Engineer',
    employer: 'Tech Solutions Inc.',

    // Billing
    billingEmail: 'billing@acme.com',
    billingCycle: 'monthly',
    paymentMethod: 'credit_card',
    nextBillingDate: '2024-11-01',
    monthlyAmount: '299.99',
    accountBalance: '0.00',

    // Activity
    lastLogin: '2024-10-15T14:30:00',
    lastContact: '2024-10-14T10:15:00',
    lastContactMethod: 'email',
    activityNotes: 'Client inquired about new services. Follow-up scheduled for next week.',

    // Documents
    idVerificationStatus: 'verified',
    idExpiryDate: '2027-03-15',
    contractSigned: true,
    contractSignedDate: '2024-01-15',
    documentNotes: 'All documents verified and up to date.',

    // Notes
    internalNotes: 'Excellent client relationship. Prefers email communication.',
    clientPreferences: 'Prefers monthly reports via email. Available for calls on Tuesdays and Thursdays.',
    alerts: '',
    followUpDate: '2024-10-25',
    followUpReason: 'Quarterly business review'
  };

  return mockValues[fieldName] || '';
};

const formatUSDate = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '';
  return formatEpochOrDateAsUsDate(value);
};

const formatDateTime = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '';
  return formatEpochOrDateAsUsDateTime(value);
};

const epochDisplayStyle = (fieldName: string): 'date' | 'datetime' => {
  const n = String(fieldName ?? '').toLowerCase();
  if (n.includes('birth')) return 'date';
  return 'datetime';
};

/**
 * Large numeric values in these fields are dollars or counts, not Unix epoch ms, but `looksLikeEpochInstant`
 * treats 7+ digit numbers as instants. Skip epoch formatting for them (e.g. Estimated Networth → date bug).
 */
function isFinancialOrCountAmountFieldName(fieldName: string): boolean {
  const n = String(fieldName || '').toLowerCase();
  if (
    /net_?worth|networth|annual_?salary|annualsalary|estimated_?net|number_?of_?collection|collection_?account/i.test(n)
  ) {
    return true;
  }
  if (n.includes('salary') && !n.includes('birth')) return true;
  return false;
}

/**
 * Parse US date format back to ISO
 */
const parseUSDate = (usDateString: string): string => {
  const [month, day, year] = usDateString.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Dynamic Form Section Component
 */
const toApiName = (fieldName: string) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/([A-Z])/g, '_$1')
    .split('_')
    .map(s => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : ''))
    .join('_') + '__c';
};

const toPascalApiName = (fieldName: string) => {
  if (!fieldName) return '';
  return fieldName.replace(/_([a-z])/g, (_, c) => c.toUpperCase()).replace(/^./, (c) => c.toUpperCase()) + '__c';
};

const NESTED_VALUE_BAG_KEYS = ['data', 'properties', 'values'] as const;

const readScalarFromObject = (obj: Record<string, unknown> | undefined, key: string): any => {
  if (!obj || !key) return undefined;
  const v = obj[key];
  if (v !== undefined && v !== null) return v;
  return undefined;
};

/** Deduplicate while preserving first-seen order (layout keys win). */
const uniqueKeys = (keys: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of keys) {
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
};

const tryKeysOnRecord = (record: Record<string, unknown>, keys: readonly string[]): any => {
  for (const k of keys) {
    if (!k) continue;
    const v = readScalarFromObject(record, k);
    if (v !== undefined) return v;
  }
  for (const bag of NESTED_VALUE_BAG_KEYS) {
    const inner = readScalarFromObject(record, bag) as Record<string, unknown> | undefined;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) continue;
    for (const k of keys) {
      const v = readScalarFromObject(inner, k);
      if (v !== undefined) return v;
    }
  }
  return undefined;
};

/**
 * Resolves a display field from flattened record and nested Gobodo `data` / `property` bags, plus
 * common API aliases (e.g. `phone` vs `PersonPhone__c`).
 */
const getRecordFieldValue = (record: Record<string, unknown>, fieldName: string): any => {
  if (!fieldName) return undefined;
  const keysForField: string[] = [fieldName, toApiName(fieldName), toPascalApiName(fieldName)];
  const camel = fieldName.replace(/__c$/, '').replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  if (camel && !keysForField.includes(camel)) keysForField.push(camel);

  const n = fieldName.toLowerCase();
  if (n === 'phone' || n === 'clientpersonalphone' || n === 'personphone') {
    keysForField.push(
      'clientPersonalPhone',
      'Client_Personal_Phone__c',
      'PersonPhone__c',
      'ClientPersonalPhone__c',
      'Phone__c',
      'Phone',
      'phone',
      'PersonPhone',
      'MobilePhone',
      'personMobilePhone'
    );
  }
  if (n === 'socialsecuritynumber' || n === 'ssn' || n.includes('ssn') || n.includes('social')) {
    keysForField.push('ssn', 'SSN', 'SSN__c', 'socialSecurityNumber', 'Social_Security_Number__c', 'custom__ssn');
  }
  if (n === 'clientname' || n === 'accountname') {
    keysForField.push('clientName', 'Name', 'Name__c', 'AccountName', 'client_name', 'AccountName__c', 'ClientName__c');
  }

  return tryKeysOnRecord(record, uniqueKeys(keysForField));
};

const normalizeLookupValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (v.relatedObject && typeof v.relatedObject === 'object') {
      const ro = v.relatedObject as Record<string, unknown>;
      return String(ro.key ?? ro.id ?? ro.label ?? '').trim();
    }
    return String(v.key ?? v.id ?? v.label ?? '').trim();
  }
  return String(value).trim();
};

const normalizeLookupLabel = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (v.relatedObject && typeof v.relatedObject === 'object') {
      const ro = v.relatedObject as Record<string, unknown>;
      return String(ro.label ?? ro.name ?? ro.key ?? ro.id ?? '').trim();
    }
    return String(v.label ?? v.name ?? v.key ?? v.id ?? '').trim();
  }
  return String(value).trim();
};

const isLikelyUserLookupField = (fieldName: string): boolean => {
  const key = String(fieldName ?? '').trim().toLowerCase();
  return (
    key.includes('owner') ||
    key.includes('manager') ||
    key.includes('createdby') ||
    key.includes('modifiedby') ||
    key === 'assignedto'
  );
};

const EMPTY_LOOKUP_USERS: User[] = [];

const sameFieldValueMap = (prev: Record<string, any>, next: Record<string, any>): boolean => {
  const nextKeys = Object.keys(next);
  if (Object.keys(prev).length !== nextKeys.length) return false;
  return nextKeys.every((k) => Object.is(prev[k], next[k]));
};

/**
 * Lenient compare used to decide whether the parent's record data has caught up with a
 * locally-saved field edit. Normalizes object shapes (lookup wrappers) and primitive casing.
 */
const valuesEquivalent = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'object' || typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a).trim() === String(b).trim();
};

const DynamicFormSection: React.FC<DynamicFormSectionProps> = ({
  section,
  recordId,
  recordData,
  className = '',
  onFieldSave,
}) => {
  // State for collapsible sections
  const [isCollapsed, setIsCollapsed] = useState(section.collapsed || false);

  const formConfigForInit = isFormSection(section) ? (section.config as FormSectionConfig) : null;
  const [fieldValues, setFieldValues] = useState<Record<string, any>>(() => {
    if (!formConfigForInit) return {};
    const initialValues: Record<string, any> = {};
    formConfigForInit.fields.forEach(field => {
      if (!field.fieldName) return; // Skip fields without a name
      if (recordData) {
        const val = getRecordFieldValue(recordData, field.fieldName);
        initialValues[field.fieldName] = val !== undefined && val !== null ? val : '';
      } else {
        initialValues[field.fieldName] = getMockFieldValue(field.fieldName, field.fieldType);
      }
    });
    return initialValues;
  });

  /**
   * Fields the user saved in this session. We preserve these local values across parent
   * `recordData` refreshes until the server-derived value actually matches (then release).
   * This avoids the "edit → save succeeds → parent refetch clobbers edit" flicker when
   * Gobodo/SF returns stale data under a different key than the one our write targets.
   */
  const locallySavedFieldsRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (!formConfigForInit) return;
    setFieldValues(prev => {
      const next: Record<string, any> = {};
      formConfigForInit.fields.forEach(field => {
        if (!field.fieldName) return;
        if (recordData) {
          const recordVal = getRecordFieldValue(recordData, field.fieldName);
          const savedLocally = locallySavedFieldsRef.current[field.fieldName];
          const hasLocalSave = Object.prototype.hasOwnProperty.call(
            locallySavedFieldsRef.current,
            field.fieldName
          );
          if (hasLocalSave) {
            if (valuesEquivalent(recordVal, savedLocally)) {
              delete locallySavedFieldsRef.current[field.fieldName];
              next[field.fieldName] = recordVal !== undefined && recordVal !== null ? recordVal : '';
            } else {
              next[field.fieldName] = savedLocally;
            }
          } else {
            next[field.fieldName] = recordVal !== undefined && recordVal !== null ? recordVal : '';
          }
        } else {
          next[field.fieldName] = prev[field.fieldName] ?? getMockFieldValue(field.fieldName, field.fieldType);
        }
      });
      return sameFieldValueMap(prev, next) ? prev : next;
    });
  }, [recordData, formConfigForInit]);

  // State for editing
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<any>('');
  const [tempLookupLabel, setTempLookupLabel] = useState('');
  const [, setMetadataVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const objects = [
      'Account',
      'CreditBlock__c',
      'Credit_Bureaus__c',
      'Unlock_Request__c',
      'Credit_Report__c',
      'Case',
      'Contact',
      'Task',
      'CSAudit__c',
      'PAWRecord__c',
      'CyberSweepClient__c',
    ];
    Promise.all(objects.map((obj) => prefetchFieldMetadata(obj)))
      .then(() => {
        if (!cancelled) setMetadataVersion((v) => v + 1);
      })
      .catch(() => {
        if (!cancelled) setMetadataVersion((v) => v + 1);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: lookupUsersData } = useQuery<User[]>({
    queryKey: ['dynamic-form', 'lookup-users'],
    queryFn: async () => gobodoUserProvider.getUsers(),
    enabled: shouldUseGobodo(),
    staleTime: 5 * 60 * 1000,
  });
  const lookupUsers = lookupUsersData ?? EMPTY_LOOKUP_USERS;
  const userLookupOptions = useMemo(
    () =>
      lookupUsers
        .map((u) => {
          const value = String(u.graphObjectKey ?? u.id ?? '').trim();
          if (!value) return null;
          const fullName = `${String(u.firstName ?? '').trim()} ${String(u.lastName ?? '').trim()}`.trim();
          const base = fullName || String(u.username ?? '').trim() || String(u.email ?? '').trim() || value;
          return { value, label: base };
        })
        .filter((x): x is { value: string; label: string } => !!x),
    [lookupUsers]
  );

  // Validate that this is a form section (after hooks)
  if (!isFormSection(section)) {
    console.error('DynamicFormSection received non-form section:', section);
    return null;
  }

  const formConfig = section.config as FormSectionConfig;

  /**
   * Handle field edit start
   */
  const handleFieldEdit = (field: FormField) => {
    const enriched = enrichField(field);
    setEditingField(field.fieldName);
    let value: unknown = fieldValues[field.fieldName] || field.defaultValue || '';
    if (field.fieldType === 'lookup') {
      setTempLookupLabel(normalizeLookupLabel(value));
      value = normalizeLookupValue(value);
    } else {
      setTempLookupLabel('');
    }

    // Special handling for date/datetime fields - convert to US format for editing
    if ((field.fieldType === 'date' || field.fieldType === 'datetime') && value) {
      value = field.fieldType === 'date' ? formatUSDate(value) : formatDateTime(value);
    }

    // For native <select> picklists, if the incoming value doesn't match any option, the browser
    // renders option #0 as visually selected while React state stays empty. Hitting Save would
    // then send an empty string. Align tempValue with what the user actually sees.
    if (
      (enriched.fieldType === 'select' || field.fieldType === 'select') &&
      Array.isArray(enriched.options) &&
      enriched.options.length > 0
    ) {
      const stringValue = value == null ? '' : String(value);
      const hasMatchingOption = enriched.options.some((opt) => String(opt.value) === stringValue);
      if (!hasMatchingOption) {
        value = enriched.options[0].value;
      }
    }

    // Multi-select: stored as ';'-delimited string (Salesforce convention). Parse to array for editing.
    if (enriched.fieldType === 'multiselect' || field.fieldType === 'multiselect') {
      if (Array.isArray(value)) {
        // already an array
      } else if (typeof value === 'string' && value.length > 0) {
        value = value.split(';').map((s) => s.trim()).filter(Boolean);
      } else {
        value = [];
      }
    }

    setTempValue(value);
  };

  /**
   * Handle field save
   */
  const handleFieldSave = async (field: FormField) => {
    let valueToSave = tempValue;

    // Multi-select: convert array back to ';'-delimited string for storage.
    if (field.fieldType === 'multiselect' && Array.isArray(tempValue)) {
      valueToSave = tempValue.filter(Boolean).join(';');
    }

    // Special handling for date fields - convert from US format back to ISO
    if (field.fieldType === 'date' && tempValue) {
      try {
        valueToSave = parseUSDate(tempValue);
      } catch (error) {
        console.warn('Invalid date format:', tempValue);
        return;
      }
    }

if (field.fieldType === 'lookup') {
      const optionMap = new Map(
        (field.options ?? []).map((opt) => [String(opt.value), String(opt.label)])
      );
      const rawLookupValue = normalizeLookupValue(tempValue);
      const lookupLabel =
        tempLookupLabel ||
        optionMap.get(rawLookupValue) ||
        normalizeLookupLabel(fieldValues[field.fieldName]) ||
        rawLookupValue;
      valueToSave = rawLookupValue ? { key: rawLookupValue, label: lookupLabel } : '';
    }

    const previousValue = fieldValues[field.fieldName];

    // Keep the edited value in the UI; revert only if the save fails.
    setFieldValues(prev => ({ ...prev, [field.fieldName]: valueToSave }));
    setEditingField(null);
    setTempValue('');
    setTempLookupLabel('');

    if (onFieldSave) {
      try {
        await onFieldSave(field.fieldName, valueToSave);
        // Lock this field's UI value against subsequent parent refreshes until the server's
        // record matches what we saved (see locallySavedFieldsRef use in the recordData effect).
        locallySavedFieldsRef.current[field.fieldName] = valueToSave;
        console.log(`Field saved via callback: ${field.fieldName} = ${valueToSave}`);
      } catch (error) {
        console.error(`Failed to save field ${field.fieldName}:`, error);
        setFieldValues(prev => ({ ...prev, [field.fieldName]: previousValue }));
      }
    } else {
      console.log(`Field saved (local only): ${field.fieldName} = ${valueToSave}`);
    }
  };

  /**
   * Handle field cancel
   */
  const handleFieldCancel = () => {
    setEditingField(null);
    setTempValue('');
    setTempLookupLabel('');
  };

  /**
   * Enrich a field config with SF metadata (type, picklist values, readonly).
   * If the field already has explicit options or a non-text fieldType, keep them.
   * Otherwise, auto-resolve from the generated SF metadata.
   */
  const enrichField = (field: FormField): FormField => {
    // Try to resolve from SF metadata — check all objects the UI uses
    const sfObjects = [
      'Account',
      'CreditBlock__c',
      'Credit_Bureaus__c',
      'Unlock_Request__c',
      'Credit_Report__c',
      'Case',
      'Contact',
      'Task',
      'CSAudit__c',
      'PAWRecord__c',
      'CyberSweepClient__c',
    ];
    for (const obj of sfObjects) {
      const meta = getFieldMeta(obj, field.fieldName);
      if (!meta) continue;

      const enriched = { ...field };

      const metaTypeLower = String(meta.type ?? '').toLowerCase();
      /** Account name and similar must never be coerced to date because SF metadata or wrong field id can mismatch. */
      const isNameLikeField = /^(clientName|accountName|clientNumber|spouse|primaryParent|socialSecurityNumber|email|phone|mobile|teamEmail)$/i.test(
        field.fieldName
      );
      // Auto-set fieldType from SF if layout left type generic (text/number/empty).
      // Gobodo often returns date/datetime as numbers; layouts may use "number" incorrectly.
      if (field.fieldType === 'text' || !field.fieldType) {
        if (!(isNameLikeField && (metaTypeLower === 'date' || metaTypeLower === 'datetime'))) {
          enriched.fieldType = meta.type as FormField['fieldType'];
        }
      }
      if (
        (field.fieldType === 'number' || field.fieldType === 'text' || !field.fieldType) &&
        (metaTypeLower === 'date' || metaTypeLower === 'datetime') &&
        !isNameLikeField
      ) {
        enriched.fieldType = metaTypeLower as FormField['fieldType'];
      }

      // Auto-populate picklist options if not explicitly set
      if (!field.options && meta.picklistValues && meta.picklistValues.length > 0) {
        enriched.options = meta.picklistValues.map(v => ({ label: v, value: v }));
      }

      if (
        enriched.fieldType === 'lookup' &&
        (!enriched.options || enriched.options.length === 0) &&
        isLikelyUserLookupField(enriched.fieldName) &&
        userLookupOptions.length > 0
      ) {
        enriched.options = userLookupOptions;
      }

      // Normalize SF/Gobodo picklist-like types to UI select.
      // Without this, fields such as Bulk_SMS_Status can render as plain text inputs.
      const metaType = String(meta.type ?? '').toLowerCase();
      const sfType = String(meta.sfType ?? '').toLowerCase();
      const isMultiPicklist =
        metaType === 'multipicklist' ||
        metaType === 'multiselect' ||
        sfType === 'multipicklist' ||
        sfType === 'multiple-select' ||
        sfType === 'enum-multiple-select';
      const isSinglePicklist =
        metaType === 'select' ||
        metaType === 'picklist' ||
        sfType === 'select' ||
        sfType === 'picklist' ||
        sfType === 'enum-select';
      if (isMultiPicklist) {
        enriched.fieldType = 'multiselect';
      } else if (isSinglePicklist || meta.picklistValues?.length) {
        enriched.fieldType = 'select';
      }

      // Auto-set readonly from SF updateable (unless explicitly set in config)
      // Don't override if config explicitly set readonly
      if (field.readonly === undefined && !meta.updateable) {
        enriched.readonly = true;
      }

      if (field.fieldType === 'ssn' || field.fieldType === 'phone' || field.fieldType === 'email' || field.fieldType === 'url' || field.fieldType === 'encrypted') {
        enriched.fieldType = field.fieldType;
      }

      /** "374" and similar are parsed as year 374 by `new Date("374")` if mis-typed as date. */
      if (/^(clientName|accountName)$/i.test(field.fieldName)) {
        enriched.fieldType = 'text';
      }

      return enriched;
    }
    return field;
  };

  /**
   * Render individual field using EditableField pattern
   */
  const renderField = (rawField: FormField) => {
    const field = enrichField(rawField);
    const lookupFieldMeta = field as FormField & { lookupObject?: string; relTypeId?: number };
    const isEditing = editingField === field.fieldName;
    const rawValue = fieldValues[field.fieldName];
    const lookupValue = field.fieldType === 'lookup' ? normalizeLookupValue(rawValue) : '';
    const lookupLabel = field.fieldType === 'lookup' ? normalizeLookupLabel(rawValue) : '';
    const optionMap = new Map(
      (field.options ?? []).map((opt) => [String(opt.value), String(opt.label)])
    );
    // Use strict check — false and 0 are valid values.
    const hasValue =
      field.fieldType === 'lookup'
        ? Boolean(lookupValue || lookupLabel)
        : rawValue !== undefined && rawValue !== null && rawValue !== '';
    let displayValue: string = hasValue ? String(rawValue) : (field.defaultValue || '—');

    // Special formatting for different field types
    if (hasValue) {
      const n = (field.fieldName || '').toLowerCase();
      if (n === 'clientname' || n === 'accountname') {
        displayValue = String(rawValue);
      } else switch (field.fieldType) {
        case 'date':
          displayValue = isFinancialOrCountAmountFieldName(field.fieldName)
            ? (typeof rawValue === 'number' && Number.isFinite(rawValue)
                ? rawValue.toLocaleString('en-US')
                : String(rawValue))
            : formatUSDate(rawValue);
          break;
        case 'datetime':
          displayValue = isFinancialOrCountAmountFieldName(field.fieldName)
            ? (typeof rawValue === 'number' && Number.isFinite(rawValue)
                ? rawValue.toLocaleString('en-US')
                : String(rawValue))
            : formatDateTime(rawValue);
          break;
        case 'currency':
          { const num = parseFloat(rawValue);
            displayValue = isNaN(num) ? String(rawValue) : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
          break;
        case 'checkbox':
          displayValue = rawValue === true || rawValue === 'true' ? 'Yes' : 'No';
          break;
        case 'lookup':
          displayValue = lookupLabel || optionMap.get(lookupValue) || lookupValue || displayValue;
          break;
        case 'select':
          // For select fields, show the option label if available, otherwise the raw value
          displayValue = optionMap.get(String(rawValue)) || String(rawValue);
          break;
        case 'multiselect': {
          // ';'-delimited string (Salesforce convention) or array; render as comma-separated.
          const parts = Array.isArray(rawValue)
            ? rawValue
            : String(rawValue).split(';').map((s) => s.trim()).filter(Boolean);
          displayValue = parts.length > 0 ? parts.join(', ') : '—';
          break;
        }
        case 'ssn':
        case 'phone':
        case 'email':
        case 'url':
        case 'textarea':
        case 'encrypted':
        case 'id':
        case 'address':
          displayValue = String(rawValue);
          break;
        default:
          if (!isFinancialOrCountAmountFieldName(field.fieldName) && looksLikeEpochInstant(rawValue)) {
            displayValue =
              epochDisplayStyle(field.fieldName) === 'date'
                ? formatUSDate(rawValue)
                : formatDateTime(rawValue);
          } else if (typeof rawValue === 'string' && rawValue.includes('<a ')) {
            const match = rawValue.match(/href="([^"]+)"/);
            displayValue = match ? match[1] : rawValue.replace(/<[^>]+>/g, '');
          }
          break;
      }
    }

    // Determine if field is readonly (from config or field definition)
    const isReadonly = field.readonly || false;

    return (
      <div key={field.id} className={`${field.width === 'full' ? 'col-span-full' : ''}`}>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          {field.fieldLabel}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>

        {isEditing ? (
          // Editing mode
          <div className="flex items-center space-x-2">
            {field.fieldType === 'lookup' ? (
                <div className="flex-1">
                  <StandardLookupField
                    field={{
                      id: field.id,
                      objectId: recordId ?? section.id,
                      fieldName: field.fieldName,
                      fieldLabel: field.fieldLabel,
                      fieldType: 'lookup',
                      required: !!field.required,
                      unique: false,
                      lookupObject: lookupFieldMeta.lookupObject,
                      relTypeId: lookupFieldMeta.relTypeId,
                      isPII: false,
                      isStandard: false,
                      isActive: true,
                      fieldLevelSecurity: {
                        visibleTo: [],
                        editableBy: [],
                      },
                      createdBy: 'system',
                      createdDate: '',
                      lastModified: '',
                    }}
                    label="" // Hide label - parent already shows it
                    value={normalizeLookupValue(tempValue)}
                    selectedOptionLabel={tempLookupLabel || lookupLabel}
                    onChange={(value) => setTempValue(value)}
                    onLookupChange={(selection: StandardLookupSelection | null) => {
                      setTempValue(selection?.value ?? '');
                      setTempLookupLabel(selection?.label ?? '');
                    }}
                    disabled={false}
                    required={field.required}
                  />
                </div>
            ) : field.fieldType === 'select' && field.options && field.options.length > 0 ? (
                <select
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                  autoFocus
                >
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </option>
                  ))}
                </select>
            ) : field.fieldType === 'multiselect' && field.options && field.options.length > 0 ? (
                <div className="flex-1 flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 max-h-40 overflow-y-auto">
                  {field.options.map((option) => {
                    const selected = Array.isArray(tempValue) && tempValue.includes(String(option.value));
                    return (
                      <label
                        key={option.value}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-sm ${
                          selected
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={option.disabled}
                          onChange={(e) => {
                            const current: string[] = Array.isArray(tempValue) ? [...tempValue] : [];
                            const v = String(option.value);
                            if (e.target.checked) {
                              if (!current.includes(v)) current.push(v);
                            } else {
                              const idx = current.indexOf(v);
                              if (idx >= 0) current.splice(idx, 1);
                            }
                            setTempValue(current);
                          }}
                          className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
            ) : field.fieldType === 'textarea' ? (
              <textarea
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                placeholder={field.placeholder}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                autoFocus
              />
            ) : field.fieldType === 'checkbox' ? (
              <input
                type="checkbox"
                checked={tempValue}
                onChange={(e) => setTempValue(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                autoFocus
              />
            ) : (
              <input
                type={
                  field.fieldType === 'date' || field.fieldType === 'datetime'
                    ? 'text'
                    : field.fieldType === 'currency'
                    ? 'number'
                    : field.fieldType === 'phone'
                    ? 'tel'
                    : field.fieldType === 'ssn' ||
                        field.fieldType === 'email' ||
                        field.fieldType === 'url' ||
                        field.fieldType === 'encrypted'
                    ? 'text'
                    : field.fieldType
                }
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                placeholder={
                  field.fieldType === 'date'
                    ? 'MM/DD/YYYY'
                    : field.fieldType === 'datetime'
                    ? 'MMM DD, YYYY HH:MM AM/PM'
                    : field.placeholder
                }
                step={field.fieldType === 'currency' ? '0.01' : undefined}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            )}
            <button
              onClick={() => handleFieldSave(field)}
              className="p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
              title="Save"
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              onClick={handleFieldCancel}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          // Display mode
          <div className="flex items-center justify-between group">
            <div className="flex items-center flex-1">
              {field.fieldType === 'checkbox' ? (
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  rawValue
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {displayValue}
                </span>
              ) : field.fieldName === 'status' && rawValue ? (
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  rawValue === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : rawValue === 'inactive'
                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    : rawValue === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                }`}>
                  {rawValue.charAt(0).toUpperCase() + rawValue.slice(1)}
                </span>
              ) : field.fieldType === 'lookup' && hasValue ? (
                <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                  {displayValue}
                </span>
              ) : displayValue.startsWith('http') ? (
                <a
                  href={displayValue}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline truncate block"
                >
                  {displayValue}
                </a>
              ) : (
                <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {displayValue}
                </p>
              )}
            </div>
            {!isReadonly && (
              <button
                onClick={() => handleFieldEdit(field)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity ml-2 flex-shrink-0"
                title={`Edit ${field.fieldLabel}`}
              >
                <Edit3 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Field help text */}
        {field.validation && field.validation.length > 0 && isEditing && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {field.validation[0].message}
          </p>
        )}
      </div>
    );
  };

  /**
   * Group fields by column
   */
  const getFieldsByColumn = (columnNumber: number): FormField[] => {
    return formConfig.fields
      .filter(field => field.column === columnNumber)
      .sort((a, b) => a.order - b.order);
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Section Header */}
      <div
        className={`flex items-center justify-between px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 rounded-t-lg ${
          section.collapsible ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/70' : ''
        }`}
        onClick={() => section.collapsible && setIsCollapsed(!isCollapsed)}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {section.label}
        </h3>
        {section.collapsible && (
          <button className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            {isCollapsed ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-6 py-4">
          {section.columns === 1 ? (
            // Single column layout
            <div className="space-y-4">
              {formConfig.fields
                .sort((a, b) => a.order - b.order)
                .map(field => renderField(field))}
            </div>
          ) : (
            // Multi-column layout (2 columns)
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Column 1 */}
              <div className="space-y-4">
                {getFieldsByColumn(1).map(field => renderField(field))}
              </div>

              {/* Column 2 */}
              <div className="space-y-4">
                {getFieldsByColumn(2).map(field => renderField(field))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DynamicFormSection;
